import React, { useState } from 'react';
import { FileInfo } from '../services/fileStorageService';
import { fileStorageService } from '../services/fileStorageService';
import DocumentViewer from './DocumentViewer';

interface ExcelDetailViewProps {
  item: any; // Can be task, plan, blueprint, or target
  itemType: 'task' | 'plan' | 'blueprint' | 'target';
  onClose: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const ExcelDetailView: React.FC<ExcelDetailViewProps> = ({
  item,
  itemType,
  onClose,
  onEdit,
  onDelete
}) => {
  const [fileUrls, setFileUrls] = useState<Record<string, string | null>>({});
  const [loadingUrls, setLoadingUrls] = useState<Record<string, boolean>>({});
  const [viewingDocument, setViewingDocument] = useState<{url: string, name: string} | null>(null);

  const generateFileUrl = async (file: any) => {
    const fileKey = `${file.id}_${file.name}_${file.size_bytes || file.size}`;
    
    if (fileUrls[fileKey]) {
      return fileUrls[fileKey];
    }

    if (loadingUrls[fileKey]) {
      return null;
    }

    try {
      setLoadingUrls(prev => ({ ...prev, [fileKey]: true }));
      
      // If file has a URL, use it directly
      if (file.url) {
        console.log('🔍 Debug - Using existing file URL:', file.url?.substring(0, 100) + '...');
        setFileUrls(prev => ({ ...prev, [fileKey]: file.url }));
        return file.url;
      }

      // For chunked files, get chunk count from database
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('../config');
      
      const fileQuery = query(
        collection(db, 'fileStorage'), 
        where('id', '==', file.id)
      );
      const fileSnapshot = await getDocs(fileQuery);
      
      if (fileSnapshot.empty) {
        throw new Error('File not found in storage');
      }
      
      const fileDoc = fileSnapshot.docs[0].data();
      const chunkCount = fileDoc.chunkCount || 0;
      
      const fileInfo = {
        id: file.id,
        name: file.name,
        url: file.url,
        size: file.size_bytes || file.size,
        type: file.mime_type || file.type,
        uploadedAt: new Date().toISOString(),
        path: '',
        isChunked: chunkCount > 0,
        chunkCount: chunkCount
      };

      console.log('🔍 Debug - Calling fileStorageService.getFileUrl with:', fileInfo);
      const url = await fileStorageService.getFileUrl(fileInfo);
      console.log('🔍 Debug - fileStorageService returned URL:', url?.substring(0, 100) + '...');
      setFileUrls(prev => ({ ...prev, [fileKey]: url }));
      return url;
    } catch (error) {
      console.error('Error generating file URL:', error);
      setFileUrls(prev => ({ ...prev, [fileKey]: 'error' }));
      return 'error';
    } finally {
      setLoadingUrls(prev => ({ ...prev, [fileKey]: false }));
    }
  };

  const handleDownloadFile = async (file: any) => {
    try {
      const url = await generateFileUrl(file);
      if (url && url !== 'error' && (url.startsWith('data:') || url.startsWith('blob:'))) {
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        link.click();
      } else if (url === 'error') {
        alert('Unable to download this file. The file may be corrupted or missing.');
      } else {
        alert('File is still loading, please try again in a moment.');
      }
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  const handleViewFile = async (file: any) => {
    try {
      console.log('🔍 Debug - handleViewFile called with file:', file);
      const url = await generateFileUrl(file);
      console.log('🔍 Debug - Generated URL:', url);
      
      if (url && url !== 'error' && (url.startsWith('data:') || url.startsWith('blob:'))) {
        // Check if it's a file type that can be viewed
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
        const viewableTypes = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'txt', 'csv'];
        
        if (viewableTypes.includes(fileExtension)) {
          console.log('🔍 Debug - File is viewable, opening DocumentViewer');
          setViewingDocument({ url, name: file.name });
        } else {
          console.log('🔍 Debug - File type not viewable, downloading instead');
          // Direct download for non-viewable files
          const link = document.createElement('a');
          link.href = url;
          link.download = file.name;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else if (url === 'error') {
        alert('Unable to load this file. The file may be corrupted or missing.');
      } else {
        // Fallback: try to download anyway
        console.log('🔍 Debug - URL generation failed, attempting direct download');
        try {
          const fallbackUrl = await generateFileUrl(file);
          if (fallbackUrl && fallbackUrl !== 'error') {
            const link = document.createElement('a');
            link.href = fallbackUrl;
            link.download = file.name;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } else {
            alert('Unable to load or download this file. Please try again.');
          }
        } catch (downloadError) {
          console.error('Download fallback failed:', downloadError);
          alert('Unable to load or download this file. Please try again.');
        }
      }
    } catch (error) {
      console.error('View failed:', error);
      // Final fallback: try direct download
      try {
        const url = await generateFileUrl(file);
        if (url && url !== 'error') {
          const link = document.createElement('a');
          link.href = url;
          link.download = file.name;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          alert('Unable to load or download this file. Please try again.');
        }
      } catch (downloadError) {
        console.error('Final download fallback failed:', downloadError);
        alert('Unable to load or download this file. Please try again.');
      }
    }
  };

  const renderItemDetails = () => {
    switch (itemType) {
      case 'task':
        return (
          <div className="bg-white border border-gray-300 shadow-lg">
            {/* Excel Column Headers */}
            <div className="flex border-b-2 border-gray-400">
              <div className="w-12 h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">
                #
              </div>
              <div className="flex-1 grid grid-cols-9">
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Title</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Assigned To</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Status</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Priority</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Due Date</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Start Date</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Completion Date</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Category</div>
                <div className="h-8 bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">Description</div>
              </div>
            </div>

            {/* Excel Row Numbers and Data */}
            <div className="flex">
              <div className="w-12 bg-gray-100 border-r border-gray-400">
                <div className="h-10 border-b border-gray-300 flex items-center justify-center text-xs font-bold text-gray-600">1</div>
              </div>
              <div className="flex-1 grid grid-cols-9">
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 font-medium bg-white">
                  {item.title || 'N/A'}
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                  {item.assignedTo || 'Unassigned'}
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm bg-white">
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${
                    item.status === 'completed' ? 'bg-green-100 text-green-800' :
                    item.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    item.status === 'blocked' ? 'bg-red-100 text-red-800' :
                    item.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {item.status || 'Not set'}
                  </span>
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm bg-white">
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${
                    item.priority === 'high' ? 'bg-red-100 text-red-800' :
                    item.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {item.priority || 'Not set'}
                  </span>
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                  {item.dueDate || 'Not set'}
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                  {item.startDate || 'Not set'}
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                  {item.completionDate || 'Not set'}
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                  {item.category || 'Not set'}
                </div>
                <div className="h-10 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white truncate">
                  {item.description && !item.description.includes('Document: data:') ? item.description : 'No description'}
                </div>
              </div>
            </div>

            {/* Additional rows for notes and dependencies */}
            {(item.notes || (item.dependencies && item.dependencies.length > 0)) && (
              <>
                {item.notes && !item.notes.includes('Document: data:') && (
                  <div className="flex">
                    <div className="w-12 bg-gray-100 border-r border-gray-400">
                      <div className="h-10 border-b border-gray-300 flex items-center justify-center text-xs font-bold text-gray-600">2</div>
                    </div>
                    <div className="flex-1 grid grid-cols-9">
                      <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-xs font-bold text-gray-600 bg-gray-100">
                        Notes
                      </div>
                      <div className="h-10 col-span-8 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                        {item.notes}
                      </div>
                    </div>
                  </div>
                )}
                {item.dependencies && item.dependencies.length > 0 && (
                  <div className="flex">
                    <div className="w-12 bg-gray-100 border-r border-gray-400">
                      <div className="h-10 border-b border-gray-300 flex items-center justify-center text-xs font-bold text-gray-600">3</div>
                    </div>
                    <div className="flex-1 grid grid-cols-9">
                      <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-xs font-bold text-gray-600 bg-gray-100">
                        Dependencies
                      </div>
                      <div className="h-10 col-span-8 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                        {item.dependencies.join(', ')}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 'plan':
        return (
          <div className="bg-white border border-gray-300 shadow-lg">
            {/* Excel Column Headers */}
            <div className="flex border-b-2 border-gray-400">
              <div className="w-12 h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">
                #
              </div>
              <div className="flex-1 grid grid-cols-7">
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Title</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Status</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Start Date</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">End Date</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Progress</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Owner</div>
                <div className="h-8 bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">Description</div>
              </div>
            </div>

            {/* Excel Row Numbers and Data */}
            <div className="flex">
              <div className="w-12 bg-gray-100 border-r border-gray-400">
                <div className="h-10 border-b border-gray-300 flex items-center justify-center text-xs font-bold text-gray-600">1</div>
              </div>
              <div className="flex-1 grid grid-cols-7">
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 font-medium bg-white">
                  {item.title || 'N/A'}
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm bg-white">
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${
                    item.status === 'completed' ? 'bg-green-100 text-green-800' :
                    item.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    item.status === 'blocked' ? 'bg-red-100 text-red-800' :
                    item.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {item.status || 'Not set'}
                  </span>
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                  {item.startDate || 'Not set'}
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                  {item.endDate || 'Not set'}
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                  <div className="flex items-center">
                    <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${item.progress || 0}%` }}
                      ></div>
                    </div>
                    <span className="text-xs">{item.progress || '0'}%</span>
                  </div>
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                  {item.owner || 'Not set'}
                </div>
                <div className="h-10 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white truncate">
                  {item.description && !item.description.includes('Document: data:') ? item.description : 'No description'}
                </div>
              </div>
            </div>
          </div>
        );

      case 'blueprint':
        return (
          <div className="bg-white border border-gray-300 shadow-lg">
            {/* Excel Column Headers */}
            <div className="flex border-b-2 border-gray-400">
              <div className="w-12 h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">
                #
              </div>
              <div className="flex-1 grid grid-cols-7">
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Title</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Status</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Version</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Created By</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Last Updated</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Category</div>
                <div className="h-8 bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">Description</div>
              </div>
            </div>

            {/* Excel Row Numbers and Data */}
            <div className="flex">
              <div className="w-12 bg-gray-100 border-r border-gray-400">
                <div className="h-10 border-b border-gray-300 flex items-center justify-center text-xs font-bold text-gray-600">1</div>
              </div>
              <div className="flex-1 grid grid-cols-7">
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 font-medium bg-white">
                  {item.title || 'N/A'}
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm bg-white">
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${
                    item.status === 'completed' ? 'bg-green-100 text-green-800' :
                    item.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    item.status === 'blocked' ? 'bg-red-100 text-red-800' :
                    item.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {item.status || 'Not set'}
                  </span>
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                  {item.version || '1.0'}
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                  {item.createdBy || 'Not set'}
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                  {item.updatedAt || 'Not set'}
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                  {item.category || 'Not set'}
                </div>
                <div className="h-10 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white truncate">
                  {item.description && !item.description.includes('Document: data:') ? item.description : 'No description'}
                </div>
              </div>
            </div>
          </div>
        );

      case 'target':
        return (
          <div className="bg-white border border-gray-300 shadow-lg">
            {/* Excel Column Headers */}
            <div className="flex border-b-2 border-gray-400">
              <div className="w-12 h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">
                #
              </div>
              <div className="flex-1 grid grid-cols-7">
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Title</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Status</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Target Date</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Progress</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Priority</div>
                <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Assigned To</div>
                <div className="h-8 bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">Description</div>
              </div>
            </div>

            {/* Excel Row Numbers and Data */}
            <div className="flex">
              <div className="w-12 bg-gray-100 border-r border-gray-400">
                <div className="h-10 border-b border-gray-300 flex items-center justify-center text-xs font-bold text-gray-600">1</div>
              </div>
              <div className="flex-1 grid grid-cols-7">
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 font-medium bg-white">
                  {item.title || 'N/A'}
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm bg-white">
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${
                    item.status === 'completed' ? 'bg-green-100 text-green-800' :
                    item.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    item.status === 'blocked' ? 'bg-red-100 text-red-800' :
                    item.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {item.status || 'Not set'}
                  </span>
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                  {item.targetDate || 'Not set'}
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                  <div className="flex items-center">
                    <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full" 
                        style={{ width: `${item.progress || 0}%` }}
                      ></div>
                    </div>
                    <span className="text-xs">{item.progress || '0'}%</span>
                  </div>
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm bg-white">
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${
                    item.priority === 'high' ? 'bg-red-100 text-red-800' :
                    item.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {item.priority || 'Not set'}
                  </span>
                </div>
                <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white">
                  {item.assignedTo || 'Not set'}
                </div>
                <div className="h-10 border-b border-gray-300 flex items-center px-2 text-sm text-gray-900 bg-white truncate">
                  {item.description && !item.description.includes('Document: data:') ? item.description : 'No description'}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return <div>Unknown item type</div>;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Excel-like Header */}
      <div className="bg-white border-b-2 border-gray-300 shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onClose}
                className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded border border-gray-300 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back
              </button>
              <div className="h-6 w-px bg-gray-400"></div>
              <h1 className="text-lg font-semibold text-gray-900">
                {itemType.charAt(0).toUpperCase() + itemType.slice(1)} Details
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded border border-blue-300 transition-colors"
                >
                  Edit
                </button>
              )}
              {onDelete && (
                <button
                  onClick={onDelete}
                  className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded border border-red-300 transition-colors"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Excel-like Content Area */}
      <div className="p-4">
        <div className="max-w-7xl mx-auto">
          {/* Item Title */}
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-900">{item.title || item.name || 'Untitled'}</h2>
            {item.description && (
              <p className="mt-1 text-sm text-gray-600">{item.description}</p>
            )}
          </div>

          {/* Excel Table */}
          {renderItemDetails()}

          {/* Attachments Section */}
          {(item.attachments && item.attachments.length > 0) && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">📎 Attachments ({item.attachments.length})</h3>
              <div className="bg-white border border-gray-300 shadow-lg">
                {/* Excel Column Headers */}
                <div className="flex border-b-2 border-gray-400">
                  <div className="w-12 h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">
                    #
                  </div>
                  <div className="flex-1 grid grid-cols-4">
                    <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">File Name</div>
                    <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Type</div>
                    <div className="h-8 bg-gray-200 border-r border-gray-400 flex items-center justify-center text-xs font-bold text-gray-600">Size</div>
                    <div className="h-8 bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">Actions</div>
                  </div>
                </div>

                {/* Excel Rows for Attachments */}
                {item.attachments.map((attachment: any, index: number) => {
                  const fileKey = `${attachment.id}_${attachment.name}_${attachment.size_bytes || attachment.size}`;
                  const isLoading = loadingUrls[fileKey];
                  
                  return (
                    <div key={index} className="flex">
                      <div className="w-12 bg-gray-100 border-r border-gray-400">
                        <div className="h-10 border-b border-gray-300 flex items-center justify-center text-xs font-bold text-gray-600">
                          {index + 1}
                        </div>
                      </div>
                      <div className="flex-1 grid grid-cols-4">
                        <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm font-medium text-gray-900 bg-white">
                          {attachment.name}
                        </div>
                        <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-500 bg-white">
                          {attachment.mime_type || attachment.type || 'Unknown'}
                        </div>
                        <div className="h-10 border-r border-gray-300 border-b border-gray-300 flex items-center px-2 text-sm text-gray-500 bg-white">
                          {attachment.size_bytes ? `${(attachment.size_bytes / 1024).toFixed(2)} KB` : 'Unknown'}
                        </div>
                        <div className="h-10 border-b border-gray-300 flex items-center px-2 text-sm text-gray-500 bg-white">
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleViewFile(attachment)}
                              disabled={isLoading}
                              className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 disabled:opacity-50"
                            >
                              {isLoading ? 'Loading...' : 'View'}
                            </button>
                            <button
                              onClick={() => handleDownloadFile(attachment)}
                              disabled={isLoading}
                              className="px-2 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded border border-green-200 disabled:opacity-50"
                            >
                              Download
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <DocumentViewer
          documentUrl={viewingDocument.url}
          fileName={viewingDocument.name}
          onClose={() => setViewingDocument(null)}
        />
      )}
    </div>
  );
};

export default ExcelDetailView;
